import { useAuth } from "react-oidc-context";
import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid2,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import minecraft from "../minecraft.svg";
import forge from "../forge.png";
//import { useManagerAvailable, useWorldUuid } from "~/components/manager";
import { green } from "@mui/material/colors";
import useApi from "../Api";
import { useManagerAvailable, useWorldUuid } from "../utilities/manager";
import InvisibleInput from "./InvisibleInput";
import CreateWorldDialog from "./CreateWorldDialog";

type UserCategory = "operators" | "players";

type DialogProps = {
  category: UserCategory;
  worldUuid: string;
  currentUsers: MinecraftUserData[];
  onClose: (boolean) => void;
};

const SelectUsersDialog: React.FC<DialogProps> = ({
  category,
  worldUuid,
  currentUsers,
  onClose,
}) => {
  const { get, put } = useApi();
  const [users, setUsers] = useState<MinecraftUserData[]>([]);
  const [selected, setSelected] = useState<string[]>(
    currentUsers.map(({ name }) => name)
  );

  useEffect(() => {
    get<{ minecraftUsers: MinecraftUserData[] }>("minecraft-users").then(
      ({ minecraftUsers }) => setUsers(minecraftUsers)
    );
  }, [get]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setSelected(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  async function save() {
    await put(`worlds/${worldUuid}/${category}`, {
      [category]: selected.map((username) =>
        users.find((minecraftUser) => minecraftUser.name === username)
      ),
    });
  }

  return (
    <Dialog open={true} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {category === "operators" ? "World operators" : "Player white-list"}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth>
          <Select
            id="add-players-select"
            multiple
            value={selected}
            onChange={handleChange}
            // input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          >
            {users.map((user) => (
              <MenuItem key={user.name} value={user.name}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={() => save().then(() => onClose(true))}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

type WorldUsersProps = {
  category: UserCategory;
  users: MinecraftUserData[];
  openSelectDialog: (
    category: UserCategory,
    currentUsers: MinecraftUserData[]
  ) => void;
};

const WorldUsers: React.FC<WorldUsersProps> = ({
  category,
  users,
  openSelectDialog,
}) => {
  return (
    <Accordion elevation={3}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="players-content"
        id="players-header"
      >
        <Typography component="span">
          {users.length}{" "}
          {category === "operators"
            ? "world operators"
            : "white-listed players"}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {users.map((user) => (
            <ListItem key={user.uuid} sx={{ alignItems: "stretch" }}>
              <ListItemText
                primary={user.name}
                secondary={user.uuid}
                sx={{ maxWidth: "80%" }}
                slotProps={{
                  secondary: { sx: { fontFamily: "monospace" } },
                }}
              />
            </ListItem>
          ))}
          <ListItem sx={{ justifyContent: "center" }}>
            <Button onClick={() => openSelectDialog(category, users)}>
              Select {category}
            </Button>
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

type PutModsData = {
  uuid: string;
  url: string;
};

type WorldProps = {
  world: WorldData;
  isCurrent: boolean;
  canLaunch: boolean;
  managerAvailable: boolean;
  updateWorld: () => Promise<void>;
};

const World: React.FC<WorldProps> = ({
  world,
  isCurrent,
  canLaunch,
  managerAvailable,
  updateWorld,
}) => {
  const {
    worldUuid,
    worldName,
    serverProfile: { minecraftVersion, modding },
  } = world;
  const { modLoader = null, modLoaderVersion = null } = modding ?? {};

  const auth = useAuth();
  const { get, post, put, patch } = useApi();
  const [showMods, setShowMods] = useState(false);
  const [showSelectUsers, setShowSelectUsers] = useState<UserCategory | null>(
    null
  );
  const [mods, setMods] = useState([]);

  useEffect(() => {
    if (showMods) {
      get(`worlds/${worldUuid}/mods`).then((data) =>
        setMods((data as any).mods)
      );
    }
  }, [get, showMods]);

  async function launch(uuid: string) {
    await post(`worlds/${uuid}/launch`);
  }

  async function stop(uuid: string) {
    await post(`manager/stop-server`);
  }

  async function fetchLog(uuid: string) {
    console.log(await get(`worlds/${uuid}/log`));
  }

  async function uploadMods(file: File) {
    const filename = file.name;
    const path = `worlds/${worldUuid}/mods`;

    const { uuid, url } = await put<PutModsData>(path, {
      filename,
    });

    const response = await fetch(url, {
      method: "PUT",
      headers: { "content-type": "application/octet-stream" },
      body: file,
    });

    if (!response.ok) {
      alert("Upload failed!");
      return;
    }

    await patch(path, { uuid, filename });
  }

  const lastPlayedAt = new Date(world.createdAt.secs_since_epoch * 1000);

  const zeroPad = (value: number) => value.toString().padStart(2, "0");
  const formatDate = (date: Date) =>
    `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")} ${date.getHours()}`;

  return (
    <>
      <Accordion elevation={2} sx={{ padding: "10px 20px", marginTop: "10px" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5">{worldName}</Typography>
          <Tooltip title={`Mineraft version: ${minecraftVersion}`}>
            <Chip
              avatar={<Avatar alt="Minecraft" src={minecraft} />}
              label={minecraftVersion}
              variant="outlined"
              sx={{ marginLeft: "20px" }}
            />
          </Tooltip>
          {modLoader === "forge" && (
            <Tooltip title={`Forge version: ${modLoaderVersion}`}>
              <Chip
                avatar={<Avatar alt="Forge" src={forge} />}
                label={modLoaderVersion}
                variant="outlined"
                sx={{ marginLeft: "20px" }}
              />
            </Tooltip>
          )}
          {isCurrent && (
            <Chip label="Online" color="success" sx={{ marginLeft: "20px" }} />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Typography>Last played:</Typography>
          <Box sx={{ paddingBottom: "20px" }}>
            {canLaunch && (
              <Button
                onClick={() => launch(worldUuid)}
                disabled={!managerAvailable}
              >
                Launch
              </Button>
            )}
            {isCurrent && (
              <Button
                onClick={() => stop(worldUuid)}
                color="error"
                variant="contained"
              >
                Stop
              </Button>
            )}
            <Button onClick={() => fetchLog(worldUuid)}>Fetch log</Button>
          </Box>
          <WorldUsers
            category="operators"
            users={world.serverProfile.operators}
            openSelectDialog={setShowSelectUsers}
          />
          <WorldUsers
            category="players"
            users={world.serverProfile.players}
            openSelectDialog={setShowSelectUsers}
          />
          {!!modding && (
            <Accordion
              elevation={3}
              onChange={(event, expanded) => setShowMods(expanded)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="mods-content"
                id="mods-header"
              >
                <Typography component="span">
                  {modding.mods.length} mods
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {mods.map((mod: any) => (
                    <ListItemButton sx={{ alignItems: "stretch" }}>
                      <ListItemText
                        primary={mod.displayName}
                        secondary={mod.description}
                        sx={{ maxWidth: "80%" }}
                      />
                      <ListItemText
                        primary={<Chip label={mod.version} />}
                        sx={{ textAlign: "right" }}
                      />
                    </ListItemButton>
                  ))}
                  <ListItem sx={{ justifyContent: "center" }}>
                    <Button
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Mods
                      <InvisibleInput
                        type="file"
                        accept=".zip"
                        onChange={(event) => uploadMods(event.target.files![0])}
                      />
                    </Button>
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </AccordionDetails>
      </Accordion>
      {showSelectUsers && (
        <SelectUsersDialog
          category={showSelectUsers}
          worldUuid={world.worldUuid}
          currentUsers={
            showSelectUsers === "operators"
              ? world.serverProfile.operators
              : world.serverProfile.players
          }
          onClose={(updated) => {
            if (updated) {
              updateWorld().then(() => setShowSelectUsers(null));
            } else {
              setShowSelectUsers(null);
            }
          }}
        />
      )}
    </>
  );
};

const Worlds: React.FC = () => {
  const { get } = useApi();
  const [worlds, setWorlds] = useState<WorldData[]>([]);
  const [createWorld, setCreateWorld] = useState(false);
  const currentWorldUuid = useWorldUuid();
  const managerAvailable = useManagerAvailable();

  function fetchWorlds() {
    get<{ worlds: WorldData[] }>("worlds").then(({ worlds }) =>
      setWorlds(worlds)
    );
  }

  useEffect(fetchWorlds, [get]);

  async function updateWorld(worldUuid: string) {
    get<{ world: WorldData }>(`worlds/${worldUuid}`).then(({ world }) => {
      setWorlds(
        worlds.map((w) => (w.worldUuid === world.worldUuid ? world : w))
      );
    });
  }

  return (
    <main>
      {worlds.map((world) => (
        <World
          key={world.worldUuid}
          world={world}
          isCurrent={world.worldUuid === currentWorldUuid}
          canLaunch={!currentWorldUuid}
          managerAvailable={managerAvailable}
          updateWorld={() => updateWorld(world.worldUuid)}
        />
      ))}
      <Box
        sx={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}
      >
        <CreateWorldDialog
          open={createWorld}
          handleClose={(worldCreated) => {
            if (worldCreated) {
              fetchWorlds();
            }
            setCreateWorld(false);
          }}
        />
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<AddIcon />}
          onClick={() => setCreateWorld(true)}
        >
          New world
        </Button>
      </Box>
    </main>
  );
};

export default Worlds;
